import { parseMetadataNumber } from "@/lib/utils/numbers";
import { parsePromotion } from "@/lib/promotion";
import type { OrderResponse, OrderResponseLineItem } from "@/store/types/order";
import type { DeliveryMethodType } from "@/store/slices/delivery.slice";

export type Ga4EcommerceItem = {
  item_id: string;
  item_name: string;
  currency: string;
  price: number;
  quantity: number;
  binding_period?: number;
};

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
    gtag?: (...args: unknown[]) => void;
  }
}

const CURRENCY = "SEK";

function getMeasurementId(): string | undefined {
  const id = import.meta.env.VITE_GA_MEASUREMENT_ID;
  return typeof id === "string" && id.trim().length > 0 ? id.trim() : undefined;
}

function getGtmId(): string | undefined {
  const id = import.meta.env.VITE_GTM_ID;
  return typeof id === "string" && id.trim().length > 0 ? id.trim() : undefined;
}

function ensureDataLayer(): Record<string, unknown>[] {
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}

function ensureGtag(): void {
  if (typeof window.gtag === "function") return;

  // Match Google's standard gtag stub (pushes Arguments, not a plain array).
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    ensureDataLayer().push(arguments as unknown as Record<string, unknown>);
  };
}

/**
 * Inherit consent from Flex website only — never re-prompt on checkout.
 * Prefers cookieconsent_status on .refurbly.se (shared across subdomains),
 * then localStorage (same-origin only, e.g. localhost).
 */
function getInheritedConsent(): "granted" | "denied" | null {
  const match = document.cookie.match(/(?:^|; )cookieconsent_status=([^;]*)/);
  if (match) {
    const status = decodeURIComponent(match[1]);
    if (status === "allow") return "granted";
    if (status === "deny") return "denied";
  }

  try {
    const stored = localStorage.getItem("userConsentChoice");
    if (stored === "granted" || stored === "denied") return stored;
  } catch {
    // private mode / blocked storage
  }

  return null;
}

/**
 * Checkout does not show a cookie banner (consent is handled on Flex).
 * Ecommerce funnel tracking has no personal data — enable analytics unless
 * the user explicitly denied cookies on the Flex website.
 */
function applyConsentDefaults(): void {
  ensureGtag();
  const inherited = getInheritedConsent();
  const analytics = inherited === "denied" ? "denied" : "granted";
  // Ads stay denied unless the user already opted in on Flex.
  const ads = inherited === "granted" ? "granted" : "denied";

  window.gtag?.("consent", "default", {
    analytics_storage: analytics,
    ad_storage: ads,
    ad_user_data: ads,
    ad_personalization: ads,
  });
}

function loadScript(src: string, id: string): Promise<void> {
  if (document.getElementById(id)) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = id;
    script.async = true;
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

let initialized = false;

/** Load gtag + optional GTM once. Safe to call multiple times. */
export function initGa4(): void {
  if (initialized || typeof window === "undefined") return;

  const measurementId = getMeasurementId();
  const gtmId = getGtmId();
  if (!measurementId && !gtmId) return;

  initialized = true;
  ensureDataLayer();
  ensureGtag();
  applyConsentDefaults();

  if (measurementId) {
    void loadScript(
      `https://www.googletagmanager.com/gtag/js?id=${measurementId}`,
      "ga4-gtag-js"
    );
    window.gtag?.("js", new Date());
    window.gtag?.("config", measurementId, {
      send_page_view: true,
      // Enables GA4 Debug View when developing locally / staging.
      debug_mode:
        import.meta.env.DEV === true ||
        import.meta.env.VITE_GA_DEBUG === "true",
    });
  }

  if (gtmId) {
    ensureDataLayer().push({
      "gtm.start": new Date().getTime(),
      event: "gtm.js",
    });
    void loadScript(
      `https://www.googletagmanager.com/gtm.js?id=${gtmId}`,
      "ga4-gtm-js"
    );
  }
}

function pushEcommerceEvent(
  event: string,
  ecommerce: Record<string, unknown>
): void {
  if (typeof window === "undefined") return;
  initGa4();
  if (!getMeasurementId() && !getGtmId()) return;

  const dataLayer = ensureDataLayer();
  // Clear previous ecommerce object (GTM recommended pattern)
  dataLayer.push({ ecommerce: null });
  dataLayer.push({
    event,
    ecommerce,
  });

  // Also send via gtag so GA4 DebugView receives events without waiting on GTM.
  if (typeof window.gtag === "function" && getMeasurementId()) {
    window.gtag("event", event, ecommerce);
  }
}

function shoppableLineItems(order: OrderResponse): OrderResponseLineItem[] {
  return (
    order.line_items?.filter((item) => item.sku_code?.startsWith("R")) || []
  );
}

function getFlexPricing(order: OrderResponse): {
  monthlyPrice: number;
  bindingPeriod: number;
  value: number;
} {
  const monthlyPrice = parseMetadataNumber(
    order.metadata?.["Flex Monthly Price"],
    0
  );
  const bindingPeriod = parseMetadataNumber(
    order.metadata?.["Flex Binding Period"],
    12
  );
  const promotion = parsePromotion(order.metadata?.promotion);
  const effectiveMonthly =
    promotion && promotion.appliedPrice < monthlyPrice
      ? promotion.appliedPrice
      : monthlyPrice;

  return {
    monthlyPrice: effectiveMonthly,
    bindingPeriod,
    // Match purchase value on flex thank-you page (monthly × binding).
    value: effectiveMonthly * bindingPeriod,
  };
}

function mapOrderItems(order: OrderResponse): Ga4EcommerceItem[] {
  const { monthlyPrice, bindingPeriod } = getFlexPricing(order);

  return shoppableLineItems(order).map((item) => ({
    item_id: item.sku_code || item.id,
    item_name: item.name || item.sku_code || "Flex product",
    currency: CURRENCY,
    price: monthlyPrice,
    quantity: item.quantity || 1,
    binding_period: bindingPeriod,
  }));
}

function shippingTierLabel(method: DeliveryMethodType): string {
  switch (method) {
    case "servicePoint":
      return "Service Point";
    case "parcelBox":
      return "Parcel Box";
    case "pickup":
      return "Pickup in Store";
    default:
      return String(method);
  }
}

const beginCheckoutFiredFor = new Set<string>();
const paymentInfoFiredFor = new Set<string>();
const viewShippingStepFiredFor = new Set<string>();
const viewPaymentStepFiredFor = new Set<string>();

function sessionFlagKey(event: string, orderId: string): string {
  return `ga4_${event}_${orderId}`;
}

function hasSessionFlag(event: string, orderId: string): boolean {
  try {
    return sessionStorage.getItem(sessionFlagKey(event, orderId)) === "1";
  } catch {
    return false;
  }
}

function setSessionFlag(event: string, orderId: string): void {
  try {
    sessionStorage.setItem(sessionFlagKey(event, orderId), "1");
  } catch {
    // private mode / blocked storage — in-memory Set still helps within the page
  }
}

function alreadyTracked(
  event: string,
  orderId: string,
  memorySet: Set<string>
): boolean {
  if (memorySet.has(orderId) || hasSessionFlag(event, orderId)) return true;
  memorySet.add(orderId);
  setSessionFlag(event, orderId);
  return false;
}

function pushCustomEvent(
  event: string,
  params: Record<string, unknown> = {}
): void {
  if (typeof window === "undefined") return;
  initGa4();
  if (!getMeasurementId() && !getGtmId()) return;

  ensureDataLayer().push({ event, ...params });

  if (typeof window.gtag === "function" && getMeasurementId()) {
    window.gtag("event", event, params);
  }
}

export function trackBeginCheckout(order: OrderResponse): void {
  // Persist across BankID full-page redirects (in-memory Set alone is not enough).
  if (!order?.id || alreadyTracked("begin_checkout", order.id, beginCheckoutFiredFor)) {
    return;
  }

  const { value } = getFlexPricing(order);
  const items = mapOrderItems(order);
  if (items.length === 0 || value <= 0) return;

  pushEcommerceEvent("begin_checkout", {
    currency: CURRENCY,
    value,
    items,
  });
}

export function trackViewShippingStep(order: OrderResponse): void {
  if (
    !order?.id ||
    alreadyTracked("view_shipping_step", order.id, viewShippingStepFiredFor)
  ) {
    return;
  }

  pushCustomEvent("view_shipping_step", {
    order_id: order.id,
  });
}

export function trackViewPaymentStep(order: OrderResponse): void {
  if (
    !order?.id ||
    alreadyTracked("view_payment_step", order.id, viewPaymentStepFiredFor)
  ) {
    return;
  }

  pushCustomEvent("view_payment_step", {
    order_id: order.id,
  });
}

export function trackAddShippingInfo(
  order: OrderResponse,
  deliveryMethod: DeliveryMethodType
): void {
  if (!order?.id) return;

  const { value } = getFlexPricing(order);
  const items = mapOrderItems(order);
  if (items.length === 0) return;

  pushEcommerceEvent("add_shipping_info", {
    currency: CURRENCY,
    value,
    shipping_tier: shippingTierLabel(deliveryMethod),
    items,
  });
}

/** Fires when the user completes the payment step (card setup + order finalize). */
export function trackAddPaymentInfo(order: OrderResponse): void {
  if (
    !order?.id ||
    alreadyTracked("add_payment_info", order.id, paymentInfoFiredFor)
  ) {
    return;
  }

  const { value } = getFlexPricing(order);
  const items = mapOrderItems(order);
  if (items.length === 0) return;

  pushEcommerceEvent("add_payment_info", {
    currency: CURRENCY,
    value,
    payment_type: "Card",
    items,
  });
}
